import { createAsyncThunk } from '@reduxjs/toolkit'
import { errorGlobal, successGlobal } from '../reducers/notifications';
import { getAuthHeader, removeTokenCookie, removeActiveTenantId } from '../../utils/tools';
import { setVerify } from '../reducers/users';

import axios from 'axios'

// Register action
export const registerUser = createAsyncThunk(
    'users/registerUser',
    async({ username, email, password, name, surname, contact, recaptchaToken }, {dispatch}) => {
        try {
            const request = await axios.post(`/api/auth/register`, {
                username, email, password, name, surname, contact, recaptchaToken
            });
            dispatch(successGlobal('Vitajte! Boli ste úspešne registrovaný.'));
            return { data: request.data.user, auth: true }
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Nastala chyba. Skúste to prosím znova.'));
            throw error;
        }
    }
);

// Setup tenant action (post-registration)
export const setupTenantProfile = createAsyncThunk(
    'users/setupTenantProfile',
    async(values, {dispatch}) => {
        try {
            const response = await axios.post(`/api/tenant/setup`, values, getAuthHeader());
            dispatch(successGlobal('Údaje o spoločnosti boli úspešne uložené.'));
            return response.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri ukladaní údajov.'));
            throw error;
        }
    }
);

// Sign In Action
export const signInUser = createAsyncThunk(
    'users/signInUser',
    async({emailOrUsername, password}, {dispatch}) => {
        try {
            // Check if user adds email or username
            let requestData = {};
            if(emailOrUsername.includes('@')) {
                requestData = { email: emailOrUsername, password: password };
            } else {
                requestData = { username: emailOrUsername, password: password };
            }
            const request = await axios.post(`/api/auth/signin`, requestData);
            // Clear any leftover tenant selection from a previous session on this browser -
            // a stale x-tenant-id belonging to a different account causes every tenant-scoped
            // request to fail with "No access to this tenant".
            removeActiveTenantId();
            dispatch(successGlobal('Vitajte! Boli ste úspešne prihlásený.'));
            return { data:request.data.user, auth:true }
        } catch(error) {
            // Error messages from backend
            dispatch(errorGlobal(error.response?.data?.message || 'Nastala chyba. Skúste to prosím znova.'));
            throw error;
        }
    }
);

// IS AUTH ACTION
export const isAuth = createAsyncThunk(
    'users/isAuth',
    async()=>{
        try{
            const request = await axios.get('/api/auth/isauth');
            return { data:request.data, auth:true }
        } catch(error){
            return { data:{},auth:false}
        }
    }
);

// Sign Out
export const signOut = createAsyncThunk(
    'users/signOut',
    async () => {
        try { await axios.post('/api/auth/logout'); } catch (_) {}
        removeTokenCookie();
        removeActiveTenantId();
    }
);

// Fetch list of tenants the user has access to
export const fetchUserTenants = createAsyncThunk(
    'users/fetchUserTenants',
    async (_, { dispatch }) => {
        try {
            const request = await axios.get('/api/user/tenants', getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal('Nepodarilo sa načítať zoznam subjektov.'));
            throw error;
        }
    }
);

// Action for resend verification email
export const resendVerification = createAsyncThunk(
    'users/resendVerification',
    async(_, {dispatch}) => {
        try {
            await axios.post('/api/user/resend-verification', {}, getAuthHeader());
            dispatch(successGlobal('Overovací email bol znovu odoslaný.'));
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri odosielaní emailu.'));
            throw error;
        }
    }
);

// Forgot password - request reset link
export const forgotPassword = createAsyncThunk(
    'users/forgotPassword',
    async ({ email }, { dispatch }) => {
        try {
            await axios.post('/api/auth/forgot-password', { email });
            dispatch(successGlobal('Email na obnovu hesla bol odoslaný. Skontrolujte svoju schránku.'));
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri odosielaní emailu.'));
            throw error;
        }
    }
);

// Reset password using token
export const resetPassword = createAsyncThunk(
    'users/resetPassword',
    async ({ token, password }, { dispatch }) => {
        try {
            await axios.post('/api/auth/reset-password', { token, password });
            dispatch(successGlobal('Heslo bolo úspešne obnovené. Môžete sa prihlásiť.'));
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri obnovení hesla.'));
            throw error;
        }
    }
);

// Update signed-in user's own profile (name, surname, contact)
export const updateUserProfile = createAsyncThunk(
    'users/updateUserProfile',
    async (values, { dispatch }) => {
        try {
            const request = await axios.patch('/api/user/profile', values, getAuthHeader());
            dispatch(successGlobal('Profil bol úspešne aktualizovaný.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri aktualizácii profilu.'));
            throw error;
        }
    }
);

// Update signed-in user's login name (username)
export const updateUserUsername = createAsyncThunk(
    'users/updateUserUsername',
    async (values, { dispatch }) => {
        try {
            const request = await axios.patch('/api/user/profile/update-username', values, getAuthHeader());
            dispatch(successGlobal('Prihlasovacie meno bolo úspešne zmenené.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri zmene prihlasovacieho mena.'));
            throw error;
        }
    }
);

// Update signed-in user's e-mail (requires re-verification)
export const updateUserEmail = createAsyncThunk(
    'users/updateUserEmail',
    async (values, { dispatch }) => {
        try {
            const request = await axios.patch('/api/user/profile/update-email', values, getAuthHeader());
            dispatch(successGlobal('E-mail bol úspešne zmenený. Skontrolujte si schránku pre jeho overenie.'));
            return request.data.user;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri zmene e-mailu.'));
            throw error;
        }
    }
);

// Update signed-in user's password
export const updateUserPassword = createAsyncThunk(
    'users/updateUserPassword',
    async (values, { dispatch }) => {
        try {
            const request = await axios.patch('/api/user/profile/update-password', values, getAuthHeader());
            dispatch(successGlobal('Heslo bolo úspešne zmenené.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Chyba pri zmene hesla.'));
            throw error;
        }
    }
);

// Action for verify account
export const accountVerify = createAsyncThunk(
    'users/accountVerify',
    async(token, {dispatch, getState}) => {
        try {
            const user  = getState().users.auth;
            await axios.get(`/api/user/verification?validation=${token}`);

            if(user) {
                dispatch(setVerify())
            }

            dispatch(successGlobal('Účet bol úspešne overený.'));
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Nastala chyba. Skúste to prosím znova.'));
            throw(error);
        }
    }
);